'use client';

import { Loader } from '@/components/ui/loader';
import { api, useQuery } from '@/lib/convex';
import type { Id } from '@/convex/_generated/dataModel';
import { Folder, FolderMinus } from 'lucide-react';
import { useMemo } from 'react';
import { ItemIcon, MenuGroup, MenuItem } from '../primitives';

/** Lazy "Move to folder →" sub-page for products. */
export function ProductFolderPicker({
  term,
  heading,
  noneLabel,
  emptyLabel,
  orgSlug,
  onPick,
  onDone,
}: {
  term: string;
  heading: string;
  noneLabel?: string;
  emptyLabel: string;
  orgSlug?: string;
  onPick: (folderId: Id<'productFolders'> | null) => void | Promise<void>;
  onDone: () => void;
}) {
  const { data: folders, isPending } = useQuery(
    api.products.queries.listProductFolders,
    orgSlug ? { orgSlug } : 'skip'
  );

  const filtered = useMemo(() => {
    const list: Array<{ _id: Id<'productFolders'>; name: string }> =
      folders ?? [];
    const lower = term.trim().toLowerCase();
    if (!lower) return list;
    return list.filter((folder) => folder.name.toLowerCase().includes(lower));
  }, [folders, term]);

  const pick = (folderId: Id<'productFolders'> | null) => {
    void Promise.resolve(onPick(folderId)).finally(onDone);
  };

  if (isPending && !folders) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader size="sm" intent="secondary" />
      </div>
    );
  }

  return (
    <MenuGroup heading={heading}>
      {noneLabel && !term.trim() && (
        <MenuItem value="product-folder:none" onSelect={() => pick(null)}>
          <ItemIcon icon={FolderMinus} />
          <span className="min-w-0 flex-1 truncate">{noneLabel}</span>
        </MenuItem>
      )}
      {filtered.map((folder) => (
        <MenuItem
          key={String(folder._id)}
          value={`product-folder:${String(folder._id)}`}
          onSelect={() => pick(folder._id)}
        >
          <ItemIcon icon={Folder} />
          <span className="min-w-0 flex-1 truncate">{folder.name}</span>
        </MenuItem>
      ))}
      {filtered.length === 0 && !noneLabel && (
        <div className="text-muted-foreground/60 px-3 py-4 text-center text-sm">
          {emptyLabel}
        </div>
      )}
    </MenuGroup>
  );
}
